/**
 * Lightweight global JS-error reporter.
 *
 * Captures uncaught errors + unhandled promise rejections and POSTs them to
 * `/api/client-errors/log` so they surface in the admin /admin/issues page.
 *
 * - Best-effort (`navigator.sendBeacon` when available, else `fetch` w/ keepalive).
 * - Per-session in-memory dedupe + per-page hard cap (max 10 reports) to
 *   defend the backend from a busted page spamming us.
 * - No auth required, the backend IP-throttles to 10 events/IP/min.
 */

const MAX_REPORTS_PER_PAGE = 10;
const seen = new Set();
let reportsSent = 0;

function endpoint() {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}/api/client-errors/log`;
}

function trim(v, n) {
    if (v == null) return null;
    const s = String(v);
    return s.length > n ? s.slice(0, n) : s;
}

function fingerprint(payload) {
    return `${payload.message || ""}::${payload.source || ""}::${payload.line || ""}`;
}

function send(payload) {
    if (reportsSent >= MAX_REPORTS_PER_PAGE) return;
    const fp = fingerprint(payload);
    if (seen.has(fp)) return;
    seen.add(fp);
    reportsSent += 1;

    const body = JSON.stringify({
        message: trim(payload.message, 500),
        stack: trim(payload.stack, 4000),
        source: trim(payload.source, 200),
        line: typeof payload.line === "number" ? payload.line : null,
        column: typeof payload.column === "number" ? payload.column : null,
        url: trim(window.location.href, 500),
        user_agent: trim(navigator.userAgent, 300),
        kind: trim(payload.kind || "error", 40),
    });

    try {
        const url = endpoint();
        if (navigator.sendBeacon) {
            const blob = new Blob([body], { type: "application/json" });
            navigator.sendBeacon(url, blob);
            return;
        }
        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
            credentials: "omit",
        }).catch(() => {
            /* non-critical */
        });
    } catch {
        /* non-critical */
    }
}

export function initClientErrorReporter() {
    if (typeof window === "undefined") return;
    if (window.__bl_err_reporter_installed) return;
    window.__bl_err_reporter_installed = true;

    window.addEventListener("error", (event) => {
        // Filter out CORS-opaque "Script error." entries — they carry no
        // info and would just clutter the snapshot.
        const msg = event?.message || "";
        if (!msg || msg === "Script error.") return;
        send({
            kind: "error",
            message: msg,
            source: event.filename,
            line: event.lineno,
            column: event.colno,
            stack: event?.error?.stack,
        });
    });

    window.addEventListener("unhandledrejection", (event) => {
        const reason = event?.reason;
        const message =
            (reason && (reason.message || String(reason))) ||
            "Unhandled promise rejection";
        send({
            kind: "unhandledrejection",
            message,
            stack: reason && reason.stack ? String(reason.stack) : null,
        });
    });
}
